import React, { useState, useRef, useEffect } from 'react';
import { Mic2, Upload, Play, Pause, X, Loader2, Check, ChevronRight } from 'lucide-react';
import { doc, getDoc, setDoc, serverTimestamp, addDoc, collection } from 'firebase/firestore';
import { db } from '../config/firebase';

const VoiceCloningModal = ({ onClose, onSuccess, user }) => {
  const [step, setStep] = useState(0); // Start with instructions (step 0)
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [inputAudio, setInputAudio] = useState(null);
  const [inputAudioDuration, setInputAudioDuration] = useState(0);
  const [inputAudioName, setInputAudioName] = useState('');
  const [inputAudioUrl, setInputAudioUrl] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [audioValid, setAudioValid] = useState(false);
  const [audioError, setAudioError] = useState('');
  const [playingPreview, setPlayingPreview] = useState(false);
  const [voiceName, setVoiceName] = useState('');
  const [voiceDescription, setVoiceDescription] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(new Audio());

  // Min/max durations
  const MIN_AUDIO_DURATION = 10; // 10 seconds
  const MAX_AUDIO_DURATION = 20; // 20 seconds

  // Clean up URLs when component unmounts
  useEffect(() => {
    return () => {
      if (inputAudioUrl) {
        URL.revokeObjectURL(inputAudioUrl);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, [inputAudioUrl]);

  // Handle recording start
  const startRecording = async () => {
    try {
      // Reset states
      setAudioError('');
      setAudioValid(false);
      
      // If there's an active recording, stop it first
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
        clearInterval(recordingTimerRef.current);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Reset recording state completely
      audioChunksRef.current = [];
      setRecordingDuration(0);

      // Clear all previous audio data
      setInputAudio(null);
      setInputAudioName('');
      if (inputAudioUrl) {
        URL.revokeObjectURL(inputAudioUrl);
      }
      setInputAudioUrl(null);
      setUploadedFile(null);

      // Setup MediaRecorder
      const options = {
        mimeType: 'audio/webm'
      };

      mediaRecorderRef.current = new MediaRecorder(stream, options);

      mediaRecorderRef.current.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      });

      // Store timestamp when recording starts
      recordingStartTimeRef.current = new Date();
      
      mediaRecorderRef.current.addEventListener('stop', () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const currentDate = new Date();
        const fileName = `voice-sample-${currentDate.toISOString()}.webm`;
        const audioFile = new File([audioBlob], fileName, { type: 'audio/webm' });
        const url = URL.createObjectURL(audioBlob);
        
        // Calculate duration
        let calculatedDuration = 0;
        if (recordingStartTimeRef.current) {
          calculatedDuration = (currentDate - recordingStartTimeRef.current) / 1000;
        }
        
        // Validate audio duration
        const finalDuration = Math.max(calculatedDuration, recordingDuration);
        const isDurationValid = finalDuration >= MIN_AUDIO_DURATION && finalDuration <= MAX_AUDIO_DURATION;
        
        setInputAudio(audioFile);
        setInputAudioDuration(finalDuration);
        setInputAudioName('Recorded Voice Sample');
        setInputAudioUrl(url);
        setAudioValid(isDurationValid);
        
        if (!isDurationValid) {
          if (finalDuration < MIN_AUDIO_DURATION) {
            setAudioError(`Recording is too short (${finalDuration.toFixed(1)}s). Please record at least ${MIN_AUDIO_DURATION} seconds.`);
          } else {
            setAudioError(`Recording is too long (${finalDuration.toFixed(1)}s). Please keep it under ${MAX_AUDIO_DURATION} seconds.`);
          }
        } else {
          setAudioError('');
        }

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      });
      
      // Start recording
      mediaRecorderRef.current.start();
      setIsRecording(true);
      
      // Start duration timer
      let startTime = Date.now();
      recordingTimerRef.current = setInterval(() => {
        const currentDuration = Math.floor((Date.now() - startTime) / 1000);
        setRecordingDuration(currentDuration);
        
        // Auto-stop when maximum duration is reached
        if (currentDuration >= MAX_AUDIO_DURATION) {
          stopRecording();
        }
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
      setAudioError('Could not access microphone. Please allow microphone access or try a different browser.');
    }
  };

  // Handle recording stop
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  };
  
  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop event
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files[0]);
    }
  };
  
  // Handle file input change
  const handleFileUpload = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files[0]);
    }
  };
  
  // Process the uploaded file
  const handleFiles = (file) => {
    if (!file) return;

    // Reset validation state
    setAudioError('');
    setAudioValid(false);

    // Check file type
    if (!file.type.match('audio/wav') && !file.type.match('audio/mpeg') && !file.type.match('audio/mp3')) {
      setAudioError('Please upload a WAV or MP3 file.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Create URL for the uploaded file
    const fileUrl = URL.createObjectURL(file);

    // Create temporary element to get duration
    const tempAudio = new Audio();
    
    // Set up temporary file info while duration loads
    setInputAudio(file);
    setInputAudioName(file.name);
    setUploadedFile(file);
    setInputAudioUrl(fileUrl);
    
    // Set event handlers before setting src
    tempAudio.addEventListener('loadedmetadata', () => {
      const duration = Math.floor(tempAudio.duration);
      
      // Validate audio duration
      const isDurationValid = duration >= MIN_AUDIO_DURATION && duration <= MAX_AUDIO_DURATION;
      
      if (!isDurationValid) {
        if (duration < MIN_AUDIO_DURATION) {
          setAudioError(`Audio is too short (${duration}s). Please use a sample of at least ${MIN_AUDIO_DURATION} seconds.`);
        } else {
          setAudioError(`Audio is too long (${duration}s). Please keep it under ${MAX_AUDIO_DURATION} seconds.`);
        }
        setAudioValid(false);
      } else {
        setAudioValid(true);
        setAudioError('');
      }

      // Set duration after validation
      setInputAudioDuration(duration);
      
      // Update the main audio player ref
      if (audioRef.current) {
        audioRef.current.src = fileUrl;
      }
    });
    
    tempAudio.addEventListener('error', () => {
      console.error("Error loading audio file");
      setAudioError('Failed to load audio file. Please try a different file.');
      setAudioValid(false);
      URL.revokeObjectURL(fileUrl);
      setUploadedFile(null);
      setInputAudio(null);
      setInputAudioUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    });
    
    // Set the source last
    tempAudio.src = fileUrl;
  };
  
  // Play/pause audio preview
  const togglePlayAudio = () => {
    if (!inputAudioUrl) return;

    if (playingPreview) {
      audioRef.current.pause();
      setPlayingPreview(false);
    } else {
      audioRef.current.src = inputAudioUrl;
      audioRef.current.play()
        .then(() => {
          setPlayingPreview(true);
          
          // Add ended event listener
          audioRef.current.onended = () => {
            setPlayingPreview(false);
          };
        })
        .catch(err => {
          console.error("Audio playback error:", err);
          setPlayingPreview(false);
        });
    }
  };

  // Format time for display
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) {
      return '0:00';
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Progress to next step
  const nextStep = () => {
    if (step === 0) {
      setStep(1); // Instructions → Step 1
    } else if (step === 1 && audioValid) {
      setStep(2); // Step 1 → Step 2
    }
  };

  // Submit the voice cloning request
  const handleSubmit = async () => {
    if (!voiceName || !voiceDescription || !consentChecked || !inputAudio) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Get a blob URL from Azure function for upload
      // Getting blob URL for upload
      const blobResponse = await fetch(
        "https://voiceops.azurewebsites.net/api/createblobobject?code=TG1_oEhJKuhbzFkJuRnUllLE8L6UdkzFBnU6Vp535PSpAzFuVq_xpA==",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            userId: user.uid,
            format: uploadedFile ? uploadedFile.type.split('/')[1] : 'webm'
          })
        }
      );

      if (!blobResponse.ok) {
        throw new Error(`Failed to get blob upload URL: ${blobResponse.status} ${blobResponse.statusText}`);
      }

      const blobData = await blobResponse.json();
      const { blobUrl, blobName } = blobData;

      if (!blobUrl || !blobName) {
        throw new Error('Invalid blob upload URL received');
      }

      // Step 2: Upload audio file to the blob URL
      const uploadResponse = await fetch(blobUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': uploadedFile ? uploadedFile.type : 'audio/webm'
        },
        body: inputAudio
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload audio to blob: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      // Step 3: Call the voice cloning Azure function
      const voiceCloningResponse = await fetch(
        "https://voiceops.azurewebsites.net/api/addvoice?code=A8zdnJHETcyuOw7Mm9WG-dmIXt4Il-P1v42t6zbr6GtjAzFuKFColA==",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            audio_url: `https://luciddenoised.blob.core.windows.net/input/${blobName}`,
            blob_name: blobName,
            userId: user.uid,
            userEmail: user.email,
            name: voiceName,
            description: voiceDescription
          })
        }
      );

      if (!voiceCloningResponse.ok) {
        throw new Error(`Voice cloning failed: ${voiceCloningResponse.status} ${voiceCloningResponse.statusText}`);
      }

      const voiceCloningResult = await voiceCloningResponse.json();

      // Create voice data for the UI - we'll use the temporary URL for now
      // In production, the server would return a preview URL
      const newVoiceData = {
        id: voiceCloningResult.voice_id || `clone-${Date.now()}`,
        "Display Name": voiceName,
        description: voiceDescription,
        preview_link: inputAudioUrl, // For demo purposes using input audio, real API would return processed audio
        created_at: serverTimestamp()
      };

      // Log the cloning attempt to Firebase - wrapped in try/catch to handle permission errors
      try {
        // Check if voice_cloning_logs collection exists, create it if it doesn't
        // Use set with auto-generated ID to avoid permission issues
        const logRef = doc(collection(db, 'voice_cloning_logs'));
        await setDoc(logRef, {
          userId: user.uid,
          userEmail: user.email || 'anonymous@user.com', // Provide fallback
          voiceName,
          description: voiceDescription,
          inputDuration: inputAudioDuration,
          inputType: uploadedFile ? 'uploaded' : 'recorded',
          blobName: blobName,
          voiceId: newVoiceData.id,
          timestamp: serverTimestamp(),
          success: "success", // Using string instead of boolean
          audioSource: uploadedFile ? uploadedFile.name : 'Recording',
          audioFormat: uploadedFile ? uploadedFile.type : 'audio/webm',
          audioSize: inputAudio.size,
          requestDetails: {
            voiceCloneRequest: true,
            requestTimestamp: serverTimestamp(),
            processingTimeMs: Date.now() - (isSubmitting ? performance.now() : 0)
          }
        });
      } catch (logError) {
        // Just log the error but don't disrupt the UI flow
        console.error('Failed to log voice cloning to Firebase:', logError);
        // Voice cloning was still successful - continue the success flow
      }

      // Show success message then add to private voices
      setShowSuccess(true);

      // After showing success for 2 seconds, call onSuccess to update parent state
      setTimeout(() => {
        onSuccess(newVoiceData);
      }, 2000);

    } catch (error) {
      console.error('Voice cloning failed:', error);
      setAudioError(`Failed to clone voice: ${error.message}. Please try again.`);

      // Log the error to Firebase
      try {
        // Use setDoc with auto-generated ID to avoid permission issues
        const logRef = doc(collection(db, 'voice_cloning_logs'));
        await setDoc(logRef, {
          userId: user.uid,
          userEmail: user.email || 'anonymous@user.com', // Provide fallback
          voiceName: voiceName || 'Unnamed Voice',
          description: voiceDescription || 'No Description',
          inputDuration: inputAudioDuration || 0,
          inputType: uploadedFile ? 'uploaded' : 'recorded',
          timestamp: serverTimestamp(),
          error: error.message || 'Unknown error',
          success: "failed", // String instead of boolean
          audioSource: uploadedFile ? uploadedFile.name : 'Recording',
          audioFormat: uploadedFile ? uploadedFile.type : 'audio/webm',
          audioSize: inputAudio ? inputAudio.size : 0,
          requestDetails: {
            voiceCloneRequest: true,
            requestTimestamp: serverTimestamp(),
            processingTimeMs: Date.now() - (isSubmitting ? performance.now() : 0),
            errorDetails: error.toString()
          }
        });
      } catch (logError) {
        console.error('Failed to log error:', logError);
        // Continue showing the error to the user
      }

      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 p-3 overflow-y-auto">
      <div className="relative bg-[#2C2F30] text-[#EAECEF] shadow-lg rounded-xl max-w-xl w-full border border-[#3A3D3E] p-0">
        {/* Header with steps indicator */}
        <div className="border-b border-[#3A3D3E] p-3">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold">Create Voice Clone</h3>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-[#3A3D3E]/50 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
          
          {/* Steps indicator */}
          <div className="mt-2 flex items-center justify-center">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${step >= 1 ? 'bg-[#178A94] text-white' : 'bg-[#3A3D3E] text-[#B0B4BB]'}`}>
              1
            </div>
            <div className={`h-1 w-12 ${step >= 2 ? 'bg-[#178A94]' : 'bg-[#3A3D3E]'}`}></div>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${step === 2 ? 'bg-[#178A94] text-white' : 'bg-[#3A3D3E] text-[#B0B4BB]'}`}>
              2
            </div>
          </div>
        </div>

        {/* Step 0: Instructions */}
        {step === 0 && (
          <div className="p-4">
            <div className="text-sm text-[#B0B4BB] bg-[#1F2123] rounded-lg p-3 border border-[#3A3D3E]">
              <p className="text-[#EAECEF] text-base mb-2">With voice cloning, you can create a <span className="text-[#178A94] font-medium">private voice model</span> that's only visible and accessible to you.</p>
              
              <p className="font-medium text-[#EAECEF] mt-3 mb-1.5">For best results:</p>
              <ul className="space-y-1.5">
                <li>• Provide a 10-20 second clear audio sample</li>
                <li>• Speak in the style you want your cloned voice to have</li>
                <li>• Record in a quiet environment with minimal background noise</li>
                <li>• Don't add any sound effects or edits to your recording for best results</li>
                <li>• Speak clearly and naturally at your normal pace</li>
              </ul>
            </div>
            
            <div className="flex justify-end mt-4">
              <button
                onClick={nextStep}
                className="flex items-center px-5 py-1.5 rounded-lg bg-[#178A94] text-white hover:bg-[#146E75] transition-colors"
              >
                Get Started <ChevronRight size={16} className="ml-1" />
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Audio Recording/Upload */}
        {step === 1 && (
          <div className="p-4">
            <h4 className="text-base font-medium mb-3">Record or Upload Voice Sample</h4>

            {/* Audio Input Methods */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
              {/* Record */}
              <div className="bg-[#1F2123] p-3 rounded-lg border border-[#3A3D3E] flex flex-col items-center">
                <div 
                  className={`w-12 h-12 rounded-full mb-2 flex items-center justify-center cursor-pointer
                           ${isRecording ? 'bg-red-600 animate-pulse' : 'bg-[#178A94] hover:bg-[#146E75]'}`}
                  onClick={isRecording ? stopRecording : startRecording}
                >
                  <Mic2 size={20} className="text-white" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-sm">{isRecording ? 'Recording...' : 'Record Audio'}</p>
                  {isRecording && (
                    <p className="text-red-400 text-xs mt-1">{formatTime(recordingDuration)}</p>
                  )}
                </div>
              </div>

              {/* Upload */}
              <div 
                className={`bg-[#1F2123] p-3 rounded-lg border ${dragActive ? 'border-[#178A94]' : 'border-[#3A3D3E]'}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="flex flex-col items-center h-full justify-center">
                  <div 
                    className="w-12 h-12 rounded-full mb-2 bg-[#2C2F30] flex items-center justify-center cursor-pointer hover:bg-[#3A3D3E]"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload size={20} className="text-[#178A94]" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-sm">Upload Audio</p>
                    <p className="text-[#7C8085] text-xs mt-0.5">WAV or MP3 files</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="audio/wav,audio/mpeg,audio/mp3"
                    onChange={handleFileUpload}
                  />
                </div>
              </div>
            </div>

            {/* Audio Preview */}
            {inputAudio && (
              <div className="bg-[#1F2123] rounded-lg p-3 mb-4 border border-[#3A3D3E]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`min-w-[32px] w-8 h-8 rounded-full flex items-center justify-center ${audioValid ? 'bg-[#178A94]' : 'bg-[#3A3D3E]'}`}>
                      <button
                        onClick={togglePlayAudio}
                        className="flex items-center justify-center"
                      >
                        {playingPreview ? <Pause size={14} className="text-white" /> : <Play size={14} className="text-white" />}
                      </button>
                    </div>
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <div className="font-medium text-sm truncate max-w-full">{inputAudioName}</div>
                      <div className="text-xs text-[#B0B4BB]">Duration: {formatTime(inputAudioDuration)}</div>
                    </div>
                  </div>
                  {audioValid && (
                    <div className="text-green-400 flex items-center">
                      <Check size={16} className="mr-1" /> Valid
                    </div>
                  )}
                </div>
                
                {audioError && (
                  <div className="mt-2 text-red-400 text-xs">
                    {audioError}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between mt-4">
              <button
                onClick={() => setStep(0)}
                className="px-3 py-1.5 border border-[#3A3D3E] text-[#B0B4BB] rounded-lg hover:bg-[#3A3D3E] transition-colors text-sm"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                disabled={!audioValid}
                className={`flex items-center px-5 py-1.5 rounded-lg text-sm ${
                  audioValid 
                    ? 'bg-[#178A94] text-white hover:bg-[#146E75]' 
                    : 'bg-[#3A3D3E] text-[#7C8085] cursor-not-allowed'
                } transition-colors`}
              >
                Next <ChevronRight size={16} className="ml-1" />
              </button>
            </div>
          </div>
        )}
        
        {/* Step 2: Voice Details */}
        {step === 2 && (
          <div className="p-4">
            <h4 className="text-base font-medium mb-3">Name Your Voice</h4>
            
            {/* Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[#EAECEF] mb-1">
                  Voice Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={voiceName}
                  onChange={(e) => setVoiceName(e.target.value)}
                  className="w-full bg-[#1F2123] border border-[#3A3D3E] rounded-lg px-3 py-1.5 text-sm text-[#EAECEF] focus:outline-none focus:border-[#178A94]"
                  placeholder="e.g. My Professional Voice"
                  maxLength={30}
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#EAECEF] mb-1">
                  Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={voiceDescription}
                  onChange={(e) => setVoiceDescription(e.target.value)}
                  className="w-full bg-[#1F2123] border border-[#3A3D3E] rounded-lg px-3 py-1.5 text-sm text-[#EAECEF] focus:outline-none focus:border-[#178A94]"
                  placeholder="Briefly describe the voice (e.g. Professional male voice for presentations)"
                  rows={2}
                  maxLength={100}
                />
              </div>
              
              {/* Consent Checkbox */}
              <div className="bg-[#1F2123] rounded-lg p-3 border border-[#3A3D3E]">
                <div className="flex items-start gap-2">
                  <div className="pt-0.5">
                    <input
                      type="checkbox"
                      id="consent"
                      checked={consentChecked}
                      onChange={(e) => setConsentChecked(e.target.checked)}
                      className="w-4 h-4 accent-[#178A94]"
                    />
                  </div>
                  <label htmlFor="consent" className="text-[#B0B4BB] text-xs">
                    I confirm that the voice I am uploading or recording is my own, and not that of any third party, including known public personalities. I understand and agree that I am solely responsible for any misuse or legal claims that arise from unauthorized voice cloning. LucidTTS (Voqai Labs Pvt Ltd) disclaims all liability arising from such misuse.
                  </label>
                </div>
              </div>
            </div>
            
            {/* Success state - shows when submission is successful */}
            {showSuccess && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
                <div className="bg-[#2C2F30] rounded-lg p-6 flex flex-col items-center max-w-md mx-4">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                    <Check size={32} className="text-green-500" />
                  </div>
                  <h3 className="text-lg font-bold text-[#EAECEF] mb-1">Voice Successfully Cloned!</h3>
                  <p className="text-[#B0B4BB] text-center text-sm">
                    Your voice has been cloned successfully. You can now use it in Generate Speech and Voice Changer.
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-between mt-4">
              <button
                onClick={() => setStep(1)}
                className="px-3 py-1.5 border border-[#3A3D3E] text-[#B0B4BB] rounded-lg hover:bg-[#3A3D3E] transition-colors text-sm"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={!voiceName || !voiceDescription || !consentChecked || isSubmitting}
                className={`flex items-center px-5 py-1.5 rounded-lg text-sm ${
                  voiceName && voiceDescription && consentChecked && !isSubmitting
                    ? 'bg-[#178A94] text-white hover:bg-[#146E75]' 
                    : 'bg-[#3A3D3E] text-[#7C8085] cursor-not-allowed'
                } transition-colors`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="mr-1.5 animate-spin" />
                    Submitting...
                  </>
                ) : 'Submit'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceCloningModal;